import React from "react";
import { formatSingleAmount } from "../../utils/currency";
import { Incident, Ratio } from "./types";

// ---- small display helpers ----

// Series colors for per-currency chart bars. Validated (CVD-safe, ≥3:1 on
// white) — do NOT swap in --adm-purple/#8b5cf6 next to blue, that pair is
// indistinguishable under deuteranopia. Green/red stay reserved for
// target status in this tab.
export const SERIES_COLORS = ["#3b82f6", "#d97706", "#7c3aed", "#6b7280"];

export function fmtMonth(month: string): string {
  const [y, m] = month.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1)).toLocaleDateString([], {
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  });
}

export function fmtDay(d: string | null): string {
  if (!d) return "—";
  try {
    // Date-only strings ("2026-07-13", the server's UTC week starts) parse
    // as UTC midnight, so they MUST also format in UTC — otherwise every
    // week label shifts a day back in negative-offset timezones (US).
    const dateOnly = /^\d{4}-\d{2}-\d{2}$/.test(d);
    return new Date(d).toLocaleDateString([], {
      month: "short",
      day: "numeric",
      year: "numeric",
      ...(dateOnly ? { timeZone: "UTC" } : {}),
    });
  } catch {
    return "—";
  }
}

export function fmtMoneyMap(map: Record<string, number>, decimals = 2): string {
  const parts = Object.entries(map || {}).map(
    ([cur, v]) => formatSingleAmount(v, cur, decimals)
  );
  return parts.length ? parts.join(" + ") : "—";
}

// Red/green status against a target; gray when there's no data yet.
export function StatusPill({ pct, target }: { pct: number | null; target: number }) {
  if (pct === null) {
    return (
      <span className="muted-text adm-fs-72" title="Not enough elapsed time yet">
        too early
      </span>
    );
  }
  const ok = pct >= target;
  return (
    <span
      style={{
        display: "inline-block",
        padding: "1px 8px",
        borderRadius: 999,
        fontSize: "0.72rem",
        fontWeight: 700,
        color: "#fff",
        background: ok ? "var(--adm-green)" : "var(--adm-red)",
      }}
      title={`Target: ${target}%+`}
    >
      {ok ? "✓" : "✗"} {pct}%
    </span>
  );
}

export function RatioCell({ ratio, target }: { ratio: Ratio; target: number }) {
  return (
    <div className="adm-nowrap" style={{ display: "flex", alignItems: "center", gap: 6 }}>
      <StatusPill pct={ratio.pct} target={target} />
      {ratio.den > 0 && (
        <span className="muted-text adm-fs-72">
          {ratio.num} of {ratio.den}
        </span>
      )}
    </div>
  );
}

export function SectionCard({ title, note, children }: { title: string; note?: string; children: React.ReactNode }) {
  return (
    <div className="adm-card adm-p-16 adm-mb-14">
      <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginBottom: 10 }}>
        <h3 style={{ margin: 0, fontSize: "0.95rem" }}>{title}</h3>
        {note && (
          <span className="muted-text adm-fs-72">
            {note}
          </span>
        )}
      </div>
      {children}
    </div>
  );
}

export function IncidentTable({ incidents }: { incidents: Incident[] }) {
  return (
    <table className="adm-table-light adm-table-compact adm-mt-6">
      <thead>
        <tr>
          <th>Failed</th>
          <th>Talent</th>
          <th>Amount</th>
          <th>Bank</th>
          <th>Reason</th>
          <th>Status</th>
        </tr>
      </thead>
      <tbody>
        {incidents.map((i) => (
          <tr key={i.payoutId}>
            <td className="adm-nowrap">{fmtDay(i.failedAt)}</td>
            <td className="adm-semibold">{i.dj?.name || "Unknown"}</td>
            <td className="adm-nowrap">{fmtMoneyMap(i.netByCurrency)}</td>
            <td>{i.bankLast4 ? `••${i.bankLast4}` : "—"}</td>
            <td className="muted-text" style={{ maxWidth: 260 }}>{i.failureMessage || "—"}</td>
            <td className="adm-nowrap">
              {i.resolved ? (
                <span className="adm-green adm-semibold adm-fs-78">
                  ✓ resolved {i.hoursToResolve !== null ? `in ${i.hoursToResolve}h` : ""}
                </span>
              ) : (
                <span className="adm-red adm-semibold adm-fs-78">open</span>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
